import {
  type CSSProperties,
  FormEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  createOrganization,
  createTodo,
  deleteTodo,
  listOrganizations,
  listTodos,
  updateTodo,
} from "./api";
import { DEFAULT_API_URL } from "./config";
import {
  clearStoredToken,
  loadStoredConnection,
  storeConnection,
  storeOrganizationId,
} from "./connectionStorage";
import { demoTodos } from "./demo";
import type {
  Connection,
  Organization,
  Todo,
  TodoDraft,
  TodoStatus,
} from "./types";

const statusMeta: Record<
  TodoStatus,
  { label: string; short: string; color: string }
> = {
  todo: { label: "To-do", short: "To-do", color: "#806aef" },
  in_progress: { label: "In progress", short: "進行中", color: "#e06148" },
  in_review: { label: "In review", short: "レビュー", color: "#d89436" },
  completed: { label: "Complete", short: "完了", color: "#3a9a78" },
};

const emptyDraft: TodoDraft = {
  title: "",
  description: "",
  status: "todo",
  parent_todo_id: "",
  due_date: "",
  hypothesis: "",
  expected_result: "",
  success_criteria: "",
  actual_result: "",
  learning: "",
};

function draftFromTodo(todo: Todo): TodoDraft {
  return {
    title: todo.title,
    description: todo.description ?? "",
    status: todo.status,
    parent_todo_id: todo.parent_todo_id ?? "",
    due_date: todo.due_date ?? "",
    hypothesis: todo.hypothesis ?? "",
    expected_result: todo.expected_result ?? "",
    success_criteria: todo.success_criteria ?? "",
    actual_result: todo.actual_result ?? "",
    learning: todo.learning ?? "",
  };
}

function relativeTime(timestamp: number) {
  const minutes = Math.floor((Date.now() - timestamp) / 60000);
  if (minutes < 60) return `${Math.max(1, minutes)}分前`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}時間前`;
  const days = Math.floor(hours / 24);
  return `${days}日前`;
}

function localDateString(date = new Date()) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function dueDateMeta(todo: Todo) {
  if (!todo.due_date) {
    return { label: "—", state: "none", title: "期限なし" };
  }
  const [, month, day] = todo.due_date.split("-");
  const settled = todo.status === "completed";
  const state = settled
    ? "settled"
    : todo.due_date < localDateString()
      ? "overdue"
      : todo.due_date === localDateString()
        ? "today"
        : "upcoming";
  const title =
    state === "overdue"
      ? `${todo.due_date}（期限切れ）`
      : state === "today"
        ? `${todo.due_date}（今日まで）`
        : todo.due_date;
  return { label: `${Number(month)}/${Number(day)}`, state, title };
}

function plainMarkdown(value?: string) {
  return (
    value
      ?.replace(/```[\s\S]*?```/g, "コード")
      .replace(/!\[([^\]]*)\]\([^)]+\)/g, "$1")
      .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
      .replace(/[#>*_`~-]/g, " ")
      .replace(/\s+/g, " ")
      .trim() ?? ""
  );
}

function InlineMarkdown({ children }: { children: string }) {
  const tokens = children.split(
    /(`[^`\n]+`|\*\*[^*\n]+\*\*|\*[^*\n]+\*|\[[^\]]+\]\(https?:\/\/[^)\s]+\))/g,
  );

  return tokens.map((token, index) => {
    if (token.startsWith("`") && token.endsWith("`")) {
      return <code key={index}>{token.slice(1, -1)}</code>;
    }
    if (token.startsWith("**") && token.endsWith("**")) {
      return <strong key={index}>{token.slice(2, -2)}</strong>;
    }
    if (token.startsWith("*") && token.endsWith("*")) {
      return <em key={index}>{token.slice(1, -1)}</em>;
    }
    const link = token.match(/^\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)$/);
    if (link) {
      return (
        <a key={index} href={link[2]} target="_blank" rel="noreferrer">
          {link[1]}
        </a>
      );
    }
    return token;
  });
}

function MarkdownPreview({ value }: { value: string }) {
  const lines = value.replace(/\r\n/g, "\n").split("\n");
  const blocks = [];
  let index = 0;

  while (index < lines.length) {
    const line = lines[index];
    if (!line.trim()) {
      index += 1;
      continue;
    }

    const fence = line.match(/^```(.*)$/);
    if (fence) {
      const code = [];
      index += 1;
      while (index < lines.length && !lines[index].startsWith("```")) {
        code.push(lines[index]);
        index += 1;
      }
      index += index < lines.length ? 1 : 0;
      blocks.push(
        <pre key={`code-${index}`} data-language={fence[1].trim()}>
          <code>{code.join("\n")}</code>
        </pre>,
      );
      continue;
    }

    const heading = line.match(/^(#{1,3})\s+(.+)$/);
    if (heading) {
      const level = heading[1].length;
      const content = <InlineMarkdown>{heading[2]}</InlineMarkdown>;
      blocks.push(
        level === 1 ? (
          <h1 key={`heading-${index}`}>{content}</h1>
        ) : level === 2 ? (
          <h2 key={`heading-${index}`}>{content}</h2>
        ) : (
          <h3 key={`heading-${index}`}>{content}</h3>
        ),
      );
      index += 1;
      continue;
    }

    if (/^[-*_]{3,}\s*$/.test(line)) {
      blocks.push(<hr key={`rule-${index}`} />);
      index += 1;
      continue;
    }

    if (/^\s*[-*]\s+/.test(line)) {
      const items = [];
      while (index < lines.length && /^\s*[-*]\s+/.test(lines[index])) {
        items.push(lines[index].replace(/^\s*[-*]\s+/, ""));
        index += 1;
      }
      blocks.push(
        <ul key={`list-${index}`}>
          {items.map((item, itemIndex) => (
            <li key={itemIndex}>
              <InlineMarkdown>{item}</InlineMarkdown>
            </li>
          ))}
        </ul>,
      );
      continue;
    }

    if (/^\s*\d+\.\s+/.test(line)) {
      const items = [];
      while (index < lines.length && /^\s*\d+\.\s+/.test(lines[index])) {
        items.push(lines[index].replace(/^\s*\d+\.\s+/, ""));
        index += 1;
      }
      blocks.push(
        <ol key={`ordered-${index}`}>
          {items.map((item, itemIndex) => (
            <li key={itemIndex}>
              <InlineMarkdown>{item}</InlineMarkdown>
            </li>
          ))}
        </ol>,
      );
      continue;
    }

    if (/^>\s?/.test(line)) {
      const quote = [];
      while (index < lines.length && /^>\s?/.test(lines[index])) {
        quote.push(lines[index].replace(/^>\s?/, ""));
        index += 1;
      }
      blocks.push(
        <blockquote key={`quote-${index}`}>
          <InlineMarkdown>{quote.join(" ")}</InlineMarkdown>
        </blockquote>,
      );
      continue;
    }

    const paragraph = [];
    while (
      index < lines.length &&
      lines[index].trim() &&
      !/^(#{1,3})\s+|^```|^\s*[-*]\s+|^\s*\d+\.\s+|^>\s?|^[-*_]{3,}\s*$/.test(
        lines[index],
      )
    ) {
      paragraph.push(lines[index]);
      index += 1;
    }
    blocks.push(
      <p key={`paragraph-${index}`}>
        {paragraph.map((part, partIndex) => (
          <span key={partIndex}>
            {partIndex > 0 && <br />}
            <InlineMarkdown>{part}</InlineMarkdown>
          </span>
        ))}
      </p>,
    );
  }

  return blocks.length ? (
    <div className="markdown-preview">{blocks}</div>
  ) : (
    <div className="markdown-preview empty">プレビューする概要がありません。</div>
  );
}

function MarkdownEditor({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  const [tab, setTab] = useState<"write" | "preview">("write");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  function wrap(before: string, after: string, placeholder: string) {
    const textarea = textareaRef.current;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selection = value.slice(start, end) || placeholder;
    onChange(
      `${value.slice(0, start)}${before}${selection}${after}${value.slice(end)}`,
    );
    window.requestAnimationFrame(() => {
      textarea.focus();
      textarea.setSelectionRange(
        start + before.length,
        start + before.length + selection.length,
      );
    });
  }

  function prefixLines(prefix: string, placeholder: string) {
    const textarea = textareaRef.current;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selection = value.slice(start, end) || placeholder;
    const replacement = selection
      .split("\n")
      .map((line) => `${prefix}${line}`)
      .join("\n");
    onChange(`${value.slice(0, start)}${replacement}${value.slice(end)}`);
    window.requestAnimationFrame(() => {
      textarea.focus();
      textarea.setSelectionRange(start, start + replacement.length);
    });
  }

  return (
    <div className="markdown-editor">
      <div className="markdown-editor-topbar">
        <div className="markdown-tabs" role="tablist" aria-label="概要の表示切り替え">
          <button
            className={tab === "write" ? "active" : ""}
            type="button"
            role="tab"
            aria-selected={tab === "write"}
            onClick={() => setTab("write")}
          >
            編集
          </button>
          <button
            className={tab === "preview" ? "active" : ""}
            type="button"
            role="tab"
            aria-selected={tab === "preview"}
            onClick={() => setTab("preview")}
          >
            プレビュー
          </button>
        </div>
        <span className="markdown-badge">Markdown</span>
      </div>
      {tab === "write" ? (
        <>
          <div className="markdown-toolbar" aria-label="Markdown書式">
            <button type="button" title="見出し" onClick={() => prefixLines("## ", "見出し")}>
              H2
            </button>
            <button type="button" title="太字" onClick={() => wrap("**", "**", "強調")}>
              <strong>B</strong>
            </button>
            <button type="button" title="斜体" onClick={() => wrap("*", "*", "テキスト")}>
              <em>I</em>
            </button>
            <span />
            <button type="button" title="箇条書き" onClick={() => prefixLines("- ", "項目")}>
              • リスト
            </button>
            <button type="button" title="引用" onClick={() => prefixLines("> ", "引用")}>
              “ 引用
            </button>
            <button
              type="button"
              title="リンク"
              onClick={() => wrap("[", "](https://)", "リンク名")}
            >
              ↗ リンク
            </button>
            <button type="button" title="コード" onClick={() => wrap("`", "`", "code")}>
              {"</>"}
            </button>
          </div>
          <textarea
            ref={textareaRef}
            value={value}
            onChange={(event) => onChange(event.target.value)}
            placeholder={"## 背景\nなぜこの実験をするのかを書きます。\n\n## 試すこと\n- 小さく試す内容\n- 確認したいこと"}
            rows={15}
            aria-label="概要（Markdown）"
          />
        </>
      ) : (
        <MarkdownPreview value={value} />
      )}
      <div className="markdown-editor-footer">
        <span>Markdownで自由に構成できます</span>
        <span>{value.length}文字</span>
      </div>
    </div>
  );
}

function Metric({
  value,
  label,
  accent,
}: {
  value: number;
  label: string;
  accent: string;
}) {
  return (
    <div className="metric">
      <span className="metric-dot" style={{ background: accent }} />
      <div>
        <strong>{value}</strong>
        <span>{label}</span>
      </div>
    </div>
  );
}

type TodoTreeNode = {
  todo: Todo;
  children: TodoTreeNode[];
};

function mergeOrganizationsWithTodoData(
  organizations: Organization[],
  todos: Todo[],
) {
  const todoOrganizationIds = [
    ...new Set(todos.map((todo) => todo.organization_id).filter(Boolean)),
  ];
  const claimedIds = new Set<string>();
  const aliases = new Map<string, string>();

  const merged = organizations.map((organization) => {
    const matchingId = todoOrganizationIds.find(
      (organizationId) =>
        !claimedIds.has(organizationId) &&
        (organizationId === organization.id ||
          organizationId.toLocaleLowerCase() ===
            organization.name.toLocaleLowerCase()),
    );
    if (!matchingId) return organization;

    claimedIds.add(matchingId);
    aliases.set(organization.id, matchingId);
    return { ...organization, id: matchingId };
  });

  todoOrganizationIds.forEach((organizationId) => {
    if (
      claimedIds.has(organizationId) ||
      merged.some((organization) => organization.id === organizationId)
    ) {
      return;
    }
    merged.push({
      id: organizationId,
      name: organizationId,
      created_at_ms: 0,
      updated_at_ms: 0,
    });
  });

  return { organizations: merged, aliases };
}

function buildTodoTree(todos: Todo[]): TodoTreeNode[] {
  const nodes = new Map(
    todos.map((todo) => [todo.id, { todo, children: [] as TodoTreeNode[] }]),
  );
  const parentIds = new Map<string, string>();
  const roots: TodoTreeNode[] = [];

  todos.forEach((todo) => {
    if (todo.parent_todo_id && nodes.has(todo.parent_todo_id)) {
      parentIds.set(todo.id, todo.parent_todo_id);
    }
  });

  todos.forEach((todo) => {
    todo.relations?.forEach((relation) => {
      if (!nodes.has(relation.todo_id)) return;

      if (
        (relation.relation_type === "follow_up" ||
          relation.relation_type === "derived_from") &&
        !parentIds.has(todo.id)
      ) {
        parentIds.set(todo.id, relation.todo_id);
      }

      if (
        relation.relation_type === "parent_of" &&
        !parentIds.has(relation.todo_id)
      ) {
        parentIds.set(relation.todo_id, todo.id);
      }
    });
  });

  nodes.forEach((node) => {
    const parentId = parentIds.get(node.todo.id);
    const parent = parentId ? nodes.get(parentId) : undefined;
    const seen = new Set([node.todo.id]);
    let ancestorId = parentId;
    while (ancestorId && !seen.has(ancestorId)) {
      seen.add(ancestorId);
      ancestorId = parentIds.get(ancestorId);
    }
    const hasCycle = Boolean(ancestorId);

    if (parent && !hasCycle) {
      parent.children.push(node);
    } else {
      roots.push(node);
    }
  });

  const sortNodes = (items: TodoTreeNode[]) => {
    items.sort((a, b) => b.todo.updated_at_ms - a.todo.updated_at_ms);
    items.forEach((item) => sortNodes(item.children));
  };
  sortNodes(roots);
  return roots;
}

function pruneTodoTree(
  nodes: TodoTreeNode[],
  matches: (todo: Todo) => boolean,
): TodoTreeNode[] {
  return nodes.flatMap((node) => {
    const children = pruneTodoTree(node.children, matches);
    return matches(node.todo) || children.length
      ? [{ ...node, children }]
      : [];
  });
}

function TreeRow({
  node,
  depth,
  expanded,
  onToggle,
  onOpen,
  onAddChild,
}: {
  node: TodoTreeNode;
  depth: number;
  expanded: Set<string>;
  onToggle: (todoId: string) => void;
  onOpen: (todo: Todo) => void;
  onAddChild: (todoId: string) => void;
}) {
  const { todo, children } = node;
  const isOpen = expanded.has(todo.id);
  const hasChildren = children.length > 0;
  const summary = plainMarkdown(todo.description);
  const dueDate = dueDateMeta(todo);

  return (
    <div
      className="tree-node"
      role="treeitem"
      aria-expanded={hasChildren ? isOpen : undefined}
      style={{ "--tree-depth": depth } as CSSProperties}
    >
      <div className="tree-row" data-depth={depth}>
        <div className="tree-indent" aria-hidden="true">
          {Array.from({ length: depth }).map((_, index) => (
            <span key={index} />
          ))}
        </div>
        <button
          className="tree-toggle"
          type="button"
          onClick={() => onToggle(todo.id)}
          disabled={!hasChildren}
          aria-label={isOpen ? "子実験を折りたたむ" : "子実験を展開する"}
        >
          {hasChildren ? (isOpen ? "⌄" : "›") : "·"}
        </button>
        <span
          className="tree-status-dot"
          style={{ background: statusMeta[todo.status].color }}
        />
        <button
          className="tree-title-button"
          type="button"
          onClick={() => onOpen(todo)}
        >
          <span className="tree-title-line">
            <strong>{todo.title}</strong>
            <span className={`status-chip status-${todo.status}`}>
              {statusMeta[todo.status].label}
            </span>
          </span>
          <span className="tree-summary">
            {summary || "概要はまだ記録されていません"}
          </span>
        </button>
        <span
          className={`tree-due ${dueDate.state}`}
          title={dueDate.title}
          aria-label={`期限 ${dueDate.title}`}
        >
          {dueDate.state === "overdue" && <span aria-hidden="true">!</span>}
          {dueDate.label}
        </span>
        <span className="tree-children">
          {children.length ? `${children.length} branches` : "leaf"}
        </span>
        <span className="tree-time">{relativeTime(todo.updated_at_ms)}</span>
        <div className="tree-actions">
          <button
            className="tree-action"
            type="button"
            onClick={() => onAddChild(todo.id)}
            aria-label={`${todo.title}に子実験を追加`}
          >
            ＋
          </button>
          <button
            className="tree-action"
            type="button"
            onClick={() => onOpen(todo)}
            aria-label={`${todo.title}を編集`}
          >
            •••
          </button>
        </div>
      </div>
      {hasChildren && isOpen && (
        <div role="group" className="tree-children-group">
          {children.map((child) => (
            <TreeRow
              key={child.todo.id}
              node={child}
              depth={depth + 1}
              expanded={expanded}
              onToggle={onToggle}
              onOpen={onOpen}
              onAddChild={onAddChild}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ConnectionDialog({
  open,
  initial,
  onConnect,
  onDemo,
  onDisconnect,
  onClose,
}: {
  open: boolean;
  initial: Connection;
  onConnect: (connection: Connection) => Promise<void>;
  onDemo: () => void;
  onDisconnect: () => void;
  onClose: () => void;
}) {
  const [value, setValue] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => setValue(initial), [initial]);
  if (!open) return null;

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      await onConnect(value);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "接続できませんでした");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" role="presentation">
      <section className="connection-modal" role="dialog" aria-modal="true">
        <div className="modal-mark">G</div>
        <p className="eyebrow">
          {initial.token ? "CONNECTION SETTINGS" : "EXPERIMENT BOARD"}
        </p>
        <h2>
          {initial.token ? (
            "接続先を設定する"
          ) : (
            <>
              小さく試して、
              <br />
              大きく学ぶ。
            </>
          )}
        </h2>
        <p className="modal-lead">
          {initial.token ? (
            "API URLやアクセストークンを変更できます。Organizationはボード上で選択します。"
          ) : (
            <>
              答えを議論するより、まず確かめる。
              <br />
              チームの仮説と学びを、ひとつの場所に。
            </>
          )}
        </p>
        <form onSubmit={submit}>
          <label>
            API URL
            <input
              value={value.apiUrl}
              onChange={(event) =>
                setValue({ ...value, apiUrl: event.target.value })
              }
              placeholder={DEFAULT_API_URL}
              required
            />
          </label>
          <label>
            Access token
            <input
              type="password"
              value={value.token}
              onChange={(event) =>
                setValue({ ...value, token: event.target.value })
              }
              placeholder="Bearer付きでも貼り付けできます"
              autoComplete="off"
              autoCapitalize="none"
              spellCheck={false}
              required
            />
          </label>
          {error && <p className="form-error">{error}</p>}
          <button className="primary full" type="submit" disabled={busy}>
            {busy ? "接続しています…" : initial.token ? "設定を反映" : "ログイン"}
          </button>
        </form>
        <div className="modal-divider">
          <span>または</span>
        </div>
        <button className="text-button" type="button" onClick={onDemo}>
          サンプルデータで試す <span>→</span>
        </button>
        {initial.token && (
          <>
            <button className="forget-token" type="button" onClick={onDisconnect}>
              ログアウトしてトークンを消去
            </button>
            <button className="close-modal" type="button" onClick={onClose}>
              閉じる
            </button>
          </>
        )}
        <p className="privacy-note">
          トークンはこのブラウザに保存され、アプリには埋め込まれません。ログアウトすると消去されます。
        </p>
      </section>
    </div>
  );
}

function OrganizationDialog({
  open,
  onClose,
  onCreate,
}: {
  open: boolean;
  onClose: () => void;
  onCreate: (name: string) => Promise<void>;
}) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setName("");
    setError("");
  }, [open]);

  if (!open) return null;

  async function submit(event: FormEvent) {
    event.preventDefault();
    const normalizedName = name.trim();
    if (!normalizedName) return;
    setBusy(true);
    setError("");
    try {
      await onCreate(normalizedName);
      onClose();
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : "Organizationを作成できませんでした",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="modal-backdrop"
      role="presentation"
      onMouseDown={onClose}
    >
      <section
        className="organization-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="organization-dialog-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <p className="eyebrow">NEW ORGANIZATION</p>
        <h2 id="organization-dialog-title">Organizationを作成</h2>
        <p className="modal-lead">
          実験をまとめる新しいワークスペースを作成します。
        </p>
        <form onSubmit={submit}>
          <label>
            Organization名
            <input
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="例：Product Team"
              maxLength={80}
              required
              autoFocus
            />
          </label>
          {error && <p className="form-error">{error}</p>}
          <div className="organization-modal-actions">
            <button
              className="secondary"
              type="button"
              onClick={onClose}
              disabled={busy}
            >
              キャンセル
            </button>
            <button
              className="primary"
              type="submit"
              disabled={busy || !name.trim()}
            >
              {busy ? "作成中…" : "作成する"}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}

function Editor({
  mode,
  todo,
  todos,
  initialParentId,
  open,
  onClose,
  onSave,
  onDelete,
}: {
  mode: "create" | "edit";
  todo: Todo | null;
  todos: Todo[];
  initialParentId: string;
  open: boolean;
  onClose: () => void;
  onSave: (draft: TodoDraft) => Promise<void>;
  onDelete?: () => Promise<void>;
}) {
  const [draft, setDraft] = useState<TodoDraft>(emptyDraft);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setDraft(
      todo
        ? draftFromTodo(todo)
        : { ...emptyDraft, parent_todo_id: initialParentId },
    );
    setError("");
  }, [todo, open, initialParentId]);

  if (!open) return null;

  function field(name: keyof TodoDraft, value: string) {
    setDraft((current) => ({ ...current, [name]: value }));
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      await onSave(draft);
      onClose();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "保存できませんでした");
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!onDelete || !window.confirm("この実験を完全に削除しますか？")) return;
    setBusy(true);
    try {
      await onDelete();
      onClose();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "削除できませんでした");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="drawer-backdrop" role="presentation" onMouseDown={onClose}>
      <aside
        className="editor"
        role="dialog"
        aria-modal="true"
        aria-labelledby="editor-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="editor-header">
          <div>
            <span className="eyebrow">
              {mode === "create" ? "NEW EXPERIMENT" : `EXP-${todo?.id.slice(0, 6)}`}
            </span>
            <h2 id="editor-title">
              {mode === "create" ? "新しい実験をつくる" : "実験を編集する"}
            </h2>
          </div>
          <button className="icon-button" type="button" onClick={onClose} aria-label="閉じる">
            ×
          </button>
        </header>
        <form className="editor-form" onSubmit={submit}>
          <label>
            実験タイトル
            <input
              className="text-input"
              value={draft.title}
              onChange={(event) => field("title", event.target.value)}
              placeholder="何を試しますか？"
              required
              autoFocus
            />
          </label>
          <div className="editor-meta-grid">
            <label>
              ステータス
              <select
                className="select-input"
                value={draft.status}
                onChange={(event) => field("status", event.target.value)}
              >
                {Object.entries(statusMeta).map(([value, meta]) => (
                  <option value={value} key={value}>
                    {meta.label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              期限（任意）
              <input
                className="date-input"
                type="date"
                value={draft.due_date}
                onChange={(event) => field("due_date", event.target.value)}
              />
            </label>
          </div>
          <label>
            親実験
            <select
              className="select-input"
              value={draft.parent_todo_id}
              onChange={(event) => field("parent_todo_id", event.target.value)}
            >
              <option value="">ルート実験として配置</option>
              {todos
                .filter((candidate) => candidate.id !== todo?.id)
                .map((candidate) => (
                  <option value={candidate.id} key={candidate.id}>
                    {candidate.title}
                  </option>
                ))}
            </select>
          </label>
          <div className="markdown-field">
            <div className="markdown-field-label">
              <span>概要</span>
              <small>背景、試すこと、結果などをひとつの場所にまとめます</small>
            </div>
            <MarkdownEditor
              value={draft.description}
              onChange={(value) => field("description", value)}
            />
          </div>
          {error && <p className="form-error">{error}</p>}
          <footer className="editor-actions">
            {mode === "edit" && (
              <button className="danger-text" type="button" onClick={remove} disabled={busy}>
                削除
              </button>
            )}
            <span />
            <button className="secondary" type="button" onClick={onClose}>
              キャンセル
            </button>
            <button className="primary" type="submit" disabled={busy}>
              {busy ? "保存中…" : mode === "create" ? "実験を追加" : "変更を保存"}
            </button>
          </footer>
        </form>
      </aside>
    </div>
  );
}

export default function App() {
  const [todos, setTodos] = useState<Todo[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [connection, setConnection] = useState<Connection>(loadStoredConnection);
  const [demo, setDemo] = useState(false);
  const [connectionOpen, setConnectionOpen] = useState(!connection.token);
  const [organizationOpen, setOrganizationOpen] = useState(false);
  const [editorMode, setEditorMode] = useState<"create" | "edit">("create");
  const [selected, setSelected] = useState<Todo | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<TodoStatus | "all">("all");
  const [loading, setLoading] = useState(Boolean(connection.token));
  const [toast, setToast] = useState("");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [initialParentId, setInitialParentId] = useState("");

  const tree = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const matches = (todo: Todo) =>
        (statusFilter === "all" || todo.status === statusFilter) &&
        (!needle ||
          [todo.title, todo.description]
            .filter(Boolean)
            .some((value) => value!.toLowerCase().includes(needle)));
    return pruneTodoTree(buildTodoTree(todos), matches);
  }, [search, statusFilter, todos]);

  const counts = useMemo(
    () =>
      Object.fromEntries(
        Object.keys(statusMeta).map((status) => [
          status,
          todos.filter((todo) => todo.status === status).length,
        ]),
      ) as Record<TodoStatus, number>,
    [todos],
  );
  useEffect(() => {
    if (!connection.token) return;
    load(connection)
      .then((loaded) => {
        if (loaded.organizationId === connection.organizationId) return;
        const resolved = { ...connection, organizationId: loaded.organizationId };
        setConnection(resolved);
        storeOrganizationId(resolved.organizationId);
      })
      .catch(() => setConnectionOpen(true));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    function focusSearch(event: KeyboardEvent) {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        document.querySelector<HTMLInputElement>(".search input")?.focus();
      }
    }
    window.addEventListener("keydown", focusSearch);
    return () => window.removeEventListener("keydown", focusSearch);
  }, []);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(""), 2400);
    return () => window.clearTimeout(timer);
  }, [toast]);

  async function load(next: Connection) {
    setLoading(true);
    try {
      const unfilteredConnection = { ...next, organizationId: "" };
      const [todoResponse, organizationResponse] = await Promise.all([
        listTodos(unfilteredConnection),
        listOrganizations(unfilteredConnection),
      ]);
      const merged = mergeOrganizationsWithTodoData(
        organizationResponse.organizations,
        todoResponse.todos,
      );
      const requestedOrganizationId =
        merged.aliases.get(next.organizationId) ?? next.organizationId;
      const organizationId =
        !requestedOrganizationId ||
        merged.organizations.some(
          (organization) => organization.id === requestedOrganizationId,
        )
          ? requestedOrganizationId
          : "";
      const visibleTodos = organizationId
        ? todoResponse.todos.filter(
            (todo) => todo.organization_id === organizationId,
          )
        : todoResponse.todos;

      setTodos(visibleTodos);
      setOrganizations(merged.organizations);
      setExpanded(
        new Set(
          visibleTodos
            .filter((todo) => !todo.parent_todo_id)
            .map((todo) => todo.id),
        ),
      );
      setDemo(false);
      return {
        todos: visibleTodos,
        organizations: merged.organizations,
        organizationId,
      };
    } finally {
      setLoading(false);
    }
  }

  async function connect(next: Connection) {
    const normalized = {
      ...next,
      apiUrl: next.apiUrl.trim().replace(/\/+$/, ""),
      token: next.token.trim().replace(/^Bearer\s+/i, ""),
    };
    const loaded = await load(normalized);
    const resolved = {
      ...normalized,
      organizationId: loaded.organizationId,
    };
    setConnection(resolved);
    storeConnection(resolved);
    setConnectionOpen(false);
    setToast("Goodness APIに接続しました");
  }

  function disconnect() {
    clearStoredToken();
    setConnection((current) => ({ ...current, token: "" }));
    setTodos([]);
    setOrganizations([]);
    setDemo(false);
    setLoading(false);
    setConnectionOpen(true);
    setToast("トークンを消去しました");
  }

  function showDemo() {
    setDemo(true);
    setTodos(demoTodos);
    setOrganizations([
      {
        id: "goodness-demo",
        name: "Goodness Lab",
        created_at_ms: Date.now(),
        updated_at_ms: Date.now(),
      },
    ]);
    setConnectionOpen(false);
    setLoading(false);
    setExpanded(
      new Set(
        demoTodos
          .filter((todo) => !todo.parent_todo_id)
          .map((todo) => todo.id),
      ),
    );
  }

  function openCreate(parentTodoId = "") {
    if (!demo && !connection.organizationId) {
      setToast("サイドバーからOrganizationを選択してください");
      return;
    }
    setSelected(null);
    setInitialParentId(parentTodoId);
    setEditorMode("create");
    setEditorOpen(true);
  }

  function openEdit(todo: Todo) {
    setSelected(todo);
    setInitialParentId("");
    setEditorMode("edit");
    setEditorOpen(true);
  }

  async function save(draft: TodoDraft) {
    if (demo) {
      if (editorMode === "create") {
        setTodos((current) => [
          {
            ...draft,
            id: `demo-${Date.now()}`,
            organization_id: "goodness-demo",
            relations: [],
            created_at_ms: Date.now(),
            updated_at_ms: Date.now(),
          },
          ...current,
        ]);
      } else if (selected) {
        setTodos((current) =>
          current.map((todo) =>
            todo.id === selected.id
              ? { ...todo, ...draft, updated_at_ms: Date.now() }
              : todo,
          ),
        );
      }
      setToast("デモボードを更新しました");
      return;
    }
    if (editorMode === "create") {
      const created = await createTodo(connection, draft);
      setTodos((current) => [created, ...current]);
      setToast("新しい実験を追加しました");
    } else if (selected) {
      const updated = await updateTodo(connection, selected.id, draft);
      setTodos((current) =>
        current.map((todo) => (todo.id === updated.id ? updated : todo)),
      );
      setToast("実験を更新しました");
    }
  }

  async function remove() {
    if (!selected) return;
    if (!demo) await deleteTodo(connection, selected.id);
    setTodos((current) => current.filter((todo) => todo.id !== selected.id));
    setToast("実験を削除しました");
  }

  function toggleTree(todoId: string) {
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(todoId)) next.delete(todoId);
      else next.add(todoId);
      return next;
    });
  }

  async function chooseOrganization(organizationId: string) {
    const next = { ...connection, organizationId };
    try {
      const loaded = await load(next);
      const resolved = { ...next, organizationId: loaded.organizationId };
      setConnection(resolved);
      storeOrganizationId(resolved.organizationId);
    } catch (reason) {
      setToast(
        reason instanceof Error ? reason.message : "Organizationを切り替えられませんでした",
      );
    }
  }

  function openOrganizationCreate() {
    if (demo) {
      setToast("Organizationの作成にはAPIへの接続が必要です");
      return;
    }
    if (!connection.token) {
      setConnectionOpen(true);
      setToast("先にGoodness APIへ接続してください");
      return;
    }
    setOrganizationOpen(true);
  }

  async function addOrganization(name: string) {
    const organization = await createOrganization(connection, name);
    setOrganizations((current) =>
      [...current.filter((item) => item.id !== organization.id), organization].sort(
        (a, b) => a.name.localeCompare(b.name, "ja"),
      ),
    );
    const next = { ...connection, organizationId: organization.id };
    setConnection(next);
    storeOrganizationId(organization.id);
    setTodos([]);
    setExpanded(new Set());
    setStatusFilter("all");
    setToast(`${organization.name}を作成しました`);
  }

  return (
    <div className="app-shell">
      <header className="topbar">
        <button className="brand" type="button" onClick={() => setStatusFilter("all")}>
          <span className="brand-mark">g</span>
          <span>goodness</span>
        </button>
        <div className="top-actions">
          {demo && <span className="demo-pill">DEMO MODE</span>}
          <label className="search">
            <span>⌕</span>
            <input
              aria-label="実験を検索"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="実験を検索"
            />
            <kbd>⌘ K</kbd>
          </label>
          <button className="settings" type="button" onClick={() => setConnectionOpen(true)}>
            <span>接続設定</span>
            <span className={`status-light ${demo || connection.token ? "online" : ""}`} />
          </button>
        </div>
      </header>

      <aside className="sidebar">
        <div className="workspace">
          <div className="workspace-copy">
            <select
              aria-label="Organizationを選択"
              value={demo ? "goodness-demo" : connection.organizationId}
              onChange={(event) => chooseOrganization(event.target.value)}
              disabled={demo}
            >
              {!demo && <option value="">すべてのOrganization</option>}
              {organizations.map((organization) => (
                <option value={organization.id} key={organization.id}>
                  {organization.name}
                </option>
              ))}
            </select>
            <span>Experiment workspace</span>
          </div>
          <button
            className="workspace-add"
            type="button"
            onClick={openOrganizationCreate}
            aria-label="Organizationを作成"
            title="Organizationを作成"
          >
            ＋
          </button>
        </div>
        <nav aria-label="ボードのフィルター">
          <button
            className={statusFilter === "all" ? "active" : ""}
            type="button"
            onClick={() => setStatusFilter("all")}
          >
            <span className="nav-icon">⌂</span>
            すべての実験
            <em>{todos.length}</em>
          </button>
          <p>ステータス</p>
          {(Object.keys(statusMeta) as TodoStatus[]).map((status) => (
            <button
              className={statusFilter === status ? "active" : ""}
              type="button"
              onClick={() => setStatusFilter(status)}
              key={status}
            >
              <span
                className="nav-dot"
                style={{ background: statusMeta[status].color }}
              />
              {statusMeta[status].label}
              <em>{counts[status]}</em>
            </button>
          ))}
        </nav>
        <p className="sidebar-footer">GOODNESS / EXPERIMENT OS</p>
      </aside>

      <main>
        <section
          className={`page-heading ${connection.token || demo ? "authenticated" : ""}`}
        >
          {!connection.token && !demo && (
            <div>
              <p className="eyebrow">EXPERIMENT BOARD</p>
              <h1>
                小さく試して、
                <br />
                <em>大きく学ぶ。</em>
              </h1>
              <p className="lead">
                答えを議論するより、まず確かめる。
                <br />
                チームの仮説と学びを、ひとつの場所に。
              </p>
            </div>
          )}
          <div className="heading-actions">
            <button
              className="primary add-button"
              type="button"
              onClick={() => openCreate()}
            >
              <span>＋</span> 新しい実験
            </button>
          </div>
        </section>

        <section className="metrics" aria-label="実験の集計">
          <Metric
            value={counts.in_progress}
            label="In progress"
            accent={statusMeta.in_progress.color}
          />
          <Metric
            value={counts.in_review}
            label="In review"
            accent={statusMeta.in_review.color}
          />
          <Metric
            value={counts.completed}
            label="Complete"
            accent={statusMeta.completed.color}
          />
          <div className="learning-rate">
            <span>概要の記録率</span>
            <strong>
              {todos.length
                ? Math.round(
                    (todos.filter((todo) => todo.description?.trim()).length /
                      todos.length) *
                      100,
                  )
                : 0}
              %
            </strong>
          </div>
        </section>

        {loading ? (
          <div className="loading-state">
            <span />
            <p>実験を読み込んでいます…</p>
          </div>
        ) : (
          <section className="tree-board" aria-label="実験ツリー">
            <header className="tree-board-header">
              <span>実験 / 概要</span>
              <span>期限</span>
              <span>枝</span>
              <span>更新</span>
              <span />
            </header>
            {tree.length ? (
              <div role="tree">
                {tree.map((node) => (
                  <TreeRow
                    key={node.todo.id}
                    node={node}
                    depth={0}
                    expanded={expanded}
                    onToggle={toggleTree}
                    onOpen={openEdit}
                    onAddChild={openCreate}
                  />
                ))}
              </div>
            ) : (
              <button
                className="empty-column"
                type="button"
                onClick={() => openCreate()}
              >
                <span>＋</span>
                <p>最初の実験を追加</p>
              </button>
            )}
          </section>
        )}
      </main>

      <ConnectionDialog
        open={connectionOpen}
        initial={connection}
        onConnect={connect}
        onDemo={showDemo}
        onDisconnect={disconnect}
        onClose={() => setConnectionOpen(false)}
      />
      <OrganizationDialog
        open={organizationOpen}
        onClose={() => setOrganizationOpen(false)}
        onCreate={addOrganization}
      />
      <Editor
        mode={editorMode}
        todo={selected}
        todos={todos}
        initialParentId={initialParentId}
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        onSave={save}
        onDelete={editorMode === "edit" ? remove : undefined}
      />
      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}
